import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import styles from './edit-domain-factors.module.scss';
import { useEffect, useState } from 'react';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { DomainFactorsForm } from '../../../modules/components/forms/domain-factors/domain-factors.form';
import { observer } from 'mobx-react';
import { useLocation, useParams } from 'react-router-dom';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { SiderSlider } from '@monorepo/base/src/components/sider-slider/sider-slider';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import routes from '../../../modules/routes/index.routes';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { DomainFactorModel } from '../../../modules/models/domain-factor.model';

export const EditDomainFactors = observer(() => {
	const { domainFactorsStore, toastsStore } = useStores<IAdminxStore>();
	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const domainFactorCrudStore = domainFactorsStore.getCrud();
	const params = useParams();
	const domainFactorId = params.domainFactorId;

	const { dispatchLog } = useLogs();
	const navigate = useNavigateParams();
	const location = useLocation();

	// reset and prefill must happen here, in the parent, because child effects run first
	useEffect(() => {
		domainFactorCrudStore.reset();
		const found = domainFactorsStore.getDomainFactors().find(factor => String(factor.getId()) === String(domainFactorId));
		if (!found) {
			domainFactorCrudStore.setIsLoading(false);
			// optional: navigate back / toast "not found"
			return;
		}
		domainFactorCrudStore.setData(
			new DomainFactorModel({
				id: found.getId(),
				domain: found.getDomain(),
				campaign_id: found.getCampaignId(),
				daily_max_loss_factor: found.getDailyMaxLossFactor(),
			})
		);
		domainFactorCrudStore.setIsLoading(false);
	}, [location, domainFactorId]);

	const domain = domainFactorCrudStore.getData().getDomain();
	const formStore = domainFactorCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isLoading = domainFactorCrudStore.getIsLoading();
	const isSuccess = domainFactorCrudStore.getIsSuccess();
	const error = domainFactorCrudStore.getHttpError();

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			navigate(`${routes.domainFactors.index()}`, {
				state: { isBlockRequest: false } as BetweenPagesStateProps,
			});
			toastsStore.addToastOnNav({ msg: `Domain factor ${domain} was successfully added`, type: DispatchLogsTypes.Success });
			domainFactorCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onEditDomainFactor = () => {
		setFormClicked(true);
		domainFactorsStore.getCrud().edit();
	};

	return (
		<SiderSlider
			onClose={() => navigate(routes.domainFactors.index(), { state: { isBlockRequest: true } as BetweenPagesStateProps })}
			isOpen={true}>
			<Page className={isLoading ? styles.loadingPage : ''}>
				<Pagebar>
					<PagebarTitle>
						<div className={styles.title}>Edit Domain Factor</div>
					</PagebarTitle>
				</Pagebar>
				<DomainFactorsForm />
			</Page>
			<BottomBar>
				{error ? <ErrorMsg error={error} /> : null}
				<BottomBarActions>
					<PrimaryButton
						className={styles.createBtn}
						onClick={() => onEditDomainFactor()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'create_domain_factor_button')] }}
						isRotateIcon={isLoading}
						icon={isLoading ? 'loading-01' : 'send-03'}>
						Save
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</SiderSlider>
	);
});
